import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Close } from '@mui/icons-material';
import { StyledAutocompleteChip } from './StyledAutocompleteChip';
import { GeoAutocompleteOption } from './GeoAutocompleteOption';
import { useGeoAutocomplete } from 'hooks';
import { GeoTargetingData } from 'types';
import { MIN_QUERY_TERM_LENGTH } from 'shared';

export type GeoAutocompleteProps = {
  label: string;
  value: GeoTargetingData[];
  onChange: (newValue: GeoTargetingData[]) => void;
};

export const GeoAutocomplete = ({ label, value, onChange }: GeoAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('');

  const { result, setSearchQuery } = useGeoAutocomplete();

  useEffect(() => {
    if (inputValue.length >= MIN_QUERY_TERM_LENGTH) {
      setSearchQuery(inputValue);
    } else {
      setSearchQuery('');
    }
  }, [setSearchQuery, inputValue]);

  const { data, isLoading } = result;

  return (
    <Autocomplete
      size="small"
      loading={isLoading}
      multiple
      autoComplete
      noOptionsText={
        inputValue.length < MIN_QUERY_TERM_LENGTH
          ? `Mindestens ${MIN_QUERY_TERM_LENGTH} Zeichen eingeben …`
          : 'No locations'
      }
      options={data || []}
      popupIcon={null}
      getOptionLabel={(option) => option.name}
      renderOption={(props, option) => <GeoAutocompleteOption key={option.id} option={option} listProps={props} />}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...props } = getTagProps({ index });
          return (
            <StyledAutocompleteChip
              key={key}
              size="small"
              color="primary"
              label={option.name}
              deleteIcon={<Close />}
              {...props}
            />
          );
        })
      }
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          height: 'auto',
          background: '#fff',
        },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          paddingTop: '10px',
        },
      }}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      renderInput={(params) => <TextField {...params} label={label} fullWidth />}
    />
  );
};
